import { getLocalizedValue } from "@repo/shared";
import type { RiskAdjustmentQuestion } from "@repo/shared/types";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import {
  type ActingRepresentative,
  RepresentativeNote,
  representativeNoteText,
} from "../representative-note";
import { T } from "./adjustment-questions-section.i18n";

/**
 * QP-180 — the model's config-driven adjustment questions.
 *
 * QP-184 (Art. 54(6)) — these are the knowledge-and-experience questions, so the
 * note about who actually answered them travels WITH them. QL-212 may move this
 * section to a global page; the note has to move with it, which is why it lives
 * here and not in the container.
 */
export function AdjustmentQuestionsSection({
  questions,
  answers,
  onAnswer,
  disabled,
  language,
  actingRepresentative,
}: {
  questions: RiskAdjustmentQuestion[];
  answers: Record<string, string>;
  onAnswer: (questionId: string, answerId: string) => void;
  disabled: boolean;
  language: string;
  actingRepresentative?: ActingRepresentative | null;
}) {
  const t = useT(T);
  const { tenantDefaultLanguage } = useUserLanguage();
  return (
    <div className="flex flex-col gap-4 pt-4 border-t">
      <Label className="text-base font-semibold">{t.adjustmentQuestions}</Label>
      {actingRepresentative && (
        <RepresentativeNote
          text={representativeNoteText(
            t.repNote,
            actingRepresentative,
            t.repGeneric,
            language,
            t.repNoteViaMember,
          )}
        />
      )}
      {questions.map((question) => (
        <div key={question.id} className="flex flex-col gap-2">
          <Label className="font-medium">
            {getLocalizedValue(question.labelI18n, language, tenantDefaultLanguage)}
          </Label>
          <RadioGroup
            value={answers[question.id] ?? ""}
            onValueChange={(answerId) => onAnswer(question.id, answerId)}
            disabled={disabled}
          >
            {question.answers.map((answer) => (
              <OptionRow key={answer.id}>
                <RadioGroupItem value={answer.id} id={`adj-${question.id}-${answer.id}`} />
                <Label
                  htmlFor={`adj-${question.id}-${answer.id}`}
                  className="font-normal cursor-pointer"
                >
                  {getLocalizedValue(answer.labelI18n, language, tenantDefaultLanguage)}
                </Label>
              </OptionRow>
            ))}
          </RadioGroup>
        </div>
      ))}
    </div>
  );
}
